import {
  createContext,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import * as TabsPrimitive from '@radix-ui/react-tabs';

import { useContainerQuery } from '../../hooks';
import { cn } from '../../lib/cn';
import { Badge } from '../Badge/Badge';
import { Select, SelectContent, SelectItem, SelectTrigger } from '../Select/Select';

import type { TabDescriptor, TabsOverflowStrategy, TabsProps, TabsVariant } from './types';

// ============================================================================
// Tokens
// ============================================================================

const TRIGGER_BASE =
  'min-h-11 px-4 @[640px]:px-5 inline-flex items-center gap-2 text-sm font-body ' +
  'font-body text-slate-600 transition-colors whitespace-nowrap ' +
  'disabled:opacity-50 disabled:cursor-not-allowed ' +
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1';

const VARIANT_TRIGGER: Record<TabsVariant, string> = {
  underline:
    'border-b-2 border-transparent -mb-px ' +
    'hover:text-secondary-950 ' +
    'data-[state=active]:border-primary-500 data-[state=active]:text-primary-600 data-[state=active]:font-medium',
  pills:
    'rounded-full ' +
    'hover:bg-slate-100 ' +
    'data-[state=active]:bg-primary-500 data-[state=active]:text-white',
  solid:
    'border border-transparent ' +
    'hover:bg-slate-50 ' +
    'data-[state=active]:bg-secondary-800 data-[state=active]:text-white data-[state=active]:border-secondary-800',
  // Segmented control: white fill on active tab, subtle ink track as list bg.
  // Used in payment tab bar (mockup sheet-07).
  segmented:
    'flex-1 justify-center rounded-lg text-ink/60 ' +
    'hover:text-ink/80 ' +
    'data-[state=active]:bg-white data-[state=active]:text-ink data-[state=active]:font-semibold data-[state=active]:shadow-sm',
};

const LIST_BASE_HORIZONTAL = 'flex';
const LIST_BASE_VERTICAL = 'flex flex-col';

const LIST_VARIANT_UNDERLINE = 'border-b border-slate-200';
const LIST_VARIANT_PILLS = 'gap-1';
const LIST_VARIANT_SOLID = 'gap-1';
// Segmented: full-width track with ink/8 background, p-1 for the thumb gap.
// Dark: the ink track is near-invisible over the charcoal canvas, so the track
// takes the raised surface tone plus a hairline ring that delineates the control
// (same treatment as SegmentedControl fullWidth).
const LIST_VARIANT_SEGMENTED =
  'gap-1 rounded-xl bg-ink/[0.06] p-1 dark:bg-slate-100 dark:ring-1 dark:ring-inset dark:ring-slate-400';

// ============================================================================
// Public root — dispatches declarative vs composed
// ============================================================================

/**
 * ResponsiveTabs — Radix-powered tabs with auto overflow strategy.
 *
 * Declarative usage (90% of cases):
 * ```tsx
 * <Tabs
 *   tabs={[
 *     { id: 'info', label: 'Informações', content: <Info /> },
 *     { id: 'activity', label: 'Atividade', content: <Activity /> },
 *   ]}
 *   value={activeTab}
 *   onValueChange={setActiveTab}
 * />
 * ```
 *
 * Composed usage (custom wrappers needed):
 * ```tsx
 * <Tabs value={v} onValueChange={setV}>
 *   <TabsList>
 *     <TabsTrigger value="a">A</TabsTrigger>
 *   </TabsList>
 *   <TabsContent value="a"><Custom /></TabsContent>
 * </Tabs>
 * ```
 *
 * See ADR-002 for architecture rationale.
 */
export function Tabs(props: TabsProps) {
  const {
    id: providedId,
    value: propValue,
    defaultValue,
    onValueChange: propOnValueChange,
    variant = 'underline',
    orientation = 'horizontal',
    overflowStrategy = 'auto',
    dropdownBreakpoint = 640,
    activationMode = 'automatic',
    className,
    ariaLabel,
  } = props;

  const generatedId = useId();
  const rootId = providedId ?? `tabs-${generatedId}`;

  // If neither controlled value nor defaultValue is provided, activate the
  // first tab automatically (declarative API only — composed API is the
  // consumer's responsibility).
  const firstTabId =
    'tabs' in props && props.tabs && props.tabs.length > 0 ? props.tabs[0]?.id : undefined;
  const resolvedDefault = defaultValue ?? (propValue === undefined ? firstTabId : undefined);

  const isControlled = propValue !== undefined;
  const [internalValue, setInternalValue] = useState<string | undefined>(resolvedDefault);
  const value = isControlled ? propValue : internalValue;

  const handleValueChange = (v: string) => {
    if (!isControlled) setInternalValue(v);
    propOnValueChange?.(v);
  };

  const baseRootProps = {
    value,
    onValueChange: handleValueChange,
    orientation,
    activationMode,
    id: rootId,
    className: cn('w-full', className),
  };

  if ('tabs' in props && props.tabs) {
    return (
      <TabsPrimitive.Root {...baseRootProps}>
        <TabsVariantContext.Provider
          value={{ variant, ariaLabel, value, onValueChange: handleValueChange }}
        >
          <TabsList
            variant={variant}
            orientation={orientation}
            overflowStrategy={overflowStrategy}
            dropdownBreakpoint={dropdownBreakpoint}
            ariaLabel={ariaLabel}
            tabs={props.tabs}
            activeValue={value}
            onValueChange={handleValueChange}
          />
          {/* Auto-rendered panels from descriptors with `content`. */}
          {props.tabs.map((tab) =>
            tab.content !== undefined ? (
              <TabsContent key={tab.id} value={tab.id}>
                {tab.content}
              </TabsContent>
            ) : null,
          )}
          {/* Manually-supplied panels (composed-within-declarative mix). */}
          {props.children}
        </TabsVariantContext.Provider>
      </TabsPrimitive.Root>
    );
  }

  return (
    <TabsPrimitive.Root {...baseRootProps}>
      <TabsVariantContext.Provider
        value={{ variant, ariaLabel, value, onValueChange: handleValueChange }}
      >
        {'children' in props ? props.children : null}
      </TabsVariantContext.Provider>
    </TabsPrimitive.Root>
  );
}

// ============================================================================
// TabsList — auto dispatch between list/scroll/dropdown
// ============================================================================

interface TabsVariantContextShape {
  variant: TabsVariant;
  ariaLabel?: string;
  value?: string;
  onValueChange?: (v: string) => void;
}
const TabsVariantContext = createContext<TabsVariantContextShape>({ variant: 'underline' });

interface TabsListProps {
  children?: ReactNode;
  variant?: TabsVariant;
  orientation?: 'horizontal' | 'vertical';
  overflowStrategy?: TabsOverflowStrategy;
  dropdownBreakpoint?: number;
  ariaLabel?: string;
  className?: string;
  // Present only when called from the declarative path.
  tabs?: TabDescriptor[];
  activeValue?: string;
  onValueChange?: (v: string) => void;
}

export function TabsList({
  children,
  variant: propVariant,
  orientation = 'horizontal',
  overflowStrategy = 'auto',
  dropdownBreakpoint = 640,
  ariaLabel: propAriaLabel,
  className,
  tabs,
  activeValue,
  onValueChange,
}: TabsListProps) {
  const ctx = useContext(TabsVariantContext);
  const variant = propVariant ?? ctx.variant;
  const ariaLabel = propAriaLabel ?? ctx.ariaLabel;
  const effectiveActiveValue = activeValue ?? ctx.value;
  const effectiveOnValueChange = onValueChange ?? ctx.onValueChange;
  const container = useContainerQuery<HTMLDivElement>(dropdownBreakpoint);
  const isNarrow = !container.matches;
  const withContainer = (content: ReactNode): ReactNode => (
    <div ref={container.ref} className="@container min-w-0">
      {content}
    </div>
  );

  // Resolve effective mode. Children count stands in for tabs.length when using
  // composed API.
  const mode: 'list' | 'scroll' | 'dropdown' = useMemo(() => {
    if (overflowStrategy === 'dropdown') return 'dropdown';
    if (overflowStrategy === 'scroll') return 'scroll';
    // auto
    const tabCount = tabs?.length ?? 0;
    if (isNarrow && tabCount > 3) return 'dropdown';
    if (isNarrow) return 'scroll';
    // desktop + not overflowing → plain list with variant underline
    return 'list';
  }, [overflowStrategy, isNarrow, tabs]);

  // Dropdown mode (needs tabs descriptors + value-change handler to populate
  // the Select primitive). Falls back to scroll/list when either is missing
  // (e.g. fully composed API without tabs descriptors).
  if (mode === 'dropdown' && tabs && effectiveOnValueChange) {
    return withContainer(
      <TabsDropdown
        tabs={tabs}
        value={effectiveActiveValue}
        onValueChange={effectiveOnValueChange}
        ariaLabel={ariaLabel}
      />,
    );
  }

  const variantClass =
    variant === 'underline'
      ? LIST_VARIANT_UNDERLINE
      : variant === 'pills'
        ? LIST_VARIANT_PILLS
        : variant === 'segmented'
          ? LIST_VARIANT_SEGMENTED
          : LIST_VARIANT_SOLID;

  if (mode === 'scroll' && orientation === 'horizontal') {
    return withContainer(
      <TabsScrollList className={cn(variantClass, className)} ariaLabel={ariaLabel}>
        {children ??
          tabs?.map((tab) => (
            <TabsTrigger
              key={tab.id}
              value={tab.id}
              disabled={tab.disabled}
              icon={tab.icon}
              badgeCount={tab.badgeCount}
              variant={variant}
            >
              {tab.label}
            </TabsTrigger>
          ))}
      </TabsScrollList>,
    );
  }

  const listBase = orientation === 'horizontal' ? LIST_BASE_HORIZONTAL : LIST_BASE_VERTICAL;

  return withContainer(
    <TabsPrimitive.List aria-label={ariaLabel} className={cn(listBase, variantClass, className)}>
      {children ??
        tabs?.map((tab) => (
          <TabsTrigger
            key={tab.id}
            value={tab.id}
            disabled={tab.disabled}
            icon={tab.icon}
            badgeCount={tab.badgeCount}
            variant={variant}
          >
            {tab.label}
          </TabsTrigger>
        ))}
    </TabsPrimitive.List>,
  );
}

// ----------------------------------------------------------------------------
// Scrollable list with fade gradients (computed from scroll state).
// ----------------------------------------------------------------------------

interface TabsScrollListProps {
  children: ReactNode;
  className?: string;
  ariaLabel?: string;
}

function TabsScrollList({ children, className, ariaLabel }: TabsScrollListProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [fade, setFade] = useState({ left: false, right: false });

  const updateFade = () => {
    const el = scrollRef.current;
    if (!el) return;
    const canScroll = el.scrollWidth > el.clientWidth + 1;
    setFade({
      left: canScroll && el.scrollLeft > 0,
      right: canScroll && el.scrollLeft + el.clientWidth < el.scrollWidth - 1,
    });
  };

  useEffect(() => {
    updateFade();
    const el = scrollRef.current;
    if (!el) return;

    const handler = () => updateFade();
    el.addEventListener('scroll', handler, { passive: true });

    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(handler) : null;
    observer?.observe(el);

    return () => {
      el.removeEventListener('scroll', handler);
      observer?.disconnect();
    };
  }, []);

  return (
    <div className="relative">
      <TabsPrimitive.List
        ref={scrollRef}
        aria-label={ariaLabel}
        className={cn(
          'flex overflow-x-auto scroll-smooth',
          'touch-pan-x',
          '[scroll-snap-type:x_proximity] [&>*]:[scroll-snap-align:start]',
          '[&::-webkit-scrollbar]:hidden [scrollbar-width:none]',
          className,
        )}
      >
        {children}
      </TabsPrimitive.List>
      <div
        className={cn(
          'pointer-events-none absolute inset-y-0 left-0 w-6',
          'bg-gradient-to-r from-white to-transparent',
          'transition-opacity duration-150',
          fade.left ? 'opacity-100' : 'opacity-0',
        )}
        data-testid="tabs-fade-left"
      />
      <div
        className={cn(
          'pointer-events-none absolute inset-y-0 right-0 w-6',
          'bg-gradient-to-l from-white to-transparent',
          'transition-opacity duration-150',
          fade.right ? 'opacity-100' : 'opacity-0',
        )}
        data-testid="tabs-fade-right"
      />
    </div>
  );
}

// ----------------------------------------------------------------------------
// Dropdown mode — reuses the Select primitive.
// ----------------------------------------------------------------------------

interface TabsDropdownProps {
  tabs: TabDescriptor[];
  value?: string;
  onValueChange: (v: string) => void;
  ariaLabel?: string;
}

function TabsDropdown({ tabs, value, onValueChange, ariaLabel }: TabsDropdownProps) {
  return (
    <div className="pb-2" role="presentation">
      <Select value={value} onValueChange={onValueChange}>
        <SelectTrigger
          aria-label={ariaLabel}
          className="w-full text-left bg-white border border-slate-300"
        >
          {tabs.find((t) => t.id === value)?.label ?? tabs[0]?.label}
        </SelectTrigger>
        <SelectContent>
          {tabs.map((tab) => (
            <SelectItem key={tab.id} value={tab.id} disabled={tab.disabled}>
              {tab.label}
              {typeof tab.badgeCount === 'number' && tab.badgeCount > 0 ? (
                <Badge variant="neutral" className="ml-2">
                  {tab.badgeCount}
                </Badge>
              ) : null}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

// ============================================================================
// TabsTrigger — Radix Trigger with icon + badge slots
// ============================================================================

export interface TabsTriggerProps {
  value: string;
  children: ReactNode;
  icon?: React.ComponentType<{ className?: string }>;
  disabled?: boolean;
  badgeCount?: number;
  variant?: TabsVariant;
  className?: string;
}

export function TabsTrigger({
  value,
  children,
  icon: Icon,
  disabled,
  badgeCount,
  variant: propVariant,
  className,
}: TabsTriggerProps) {
  const ctx = useContext(TabsVariantContext);
  const variant = propVariant ?? ctx.variant;
  const showBadge = typeof badgeCount === 'number' && badgeCount > 0;

  return (
    <TabsPrimitive.Trigger
      value={value}
      disabled={disabled}
      className={cn(TRIGGER_BASE, VARIANT_TRIGGER[variant], className)}
    >
      {Icon ? <Icon className="h-4 w-4 shrink-0" aria-hidden /> : null}
      <span className="truncate">{children}</span>
      {showBadge ? (
        <Badge variant="neutral" className="ml-0.5">
          {badgeCount}
        </Badge>
      ) : null}
    </TabsPrimitive.Trigger>
  );
}

// ============================================================================
// TabsContent — passthrough with sensible default padding-top
// ============================================================================

export interface TabsContentProps {
  value: string;
  children: ReactNode;
  forceMount?: true;
  className?: string;
}

export function TabsContent({ value, children, forceMount, className }: TabsContentProps) {
  return (
    <TabsPrimitive.Content
      value={value}
      forceMount={forceMount}
      className={cn('pt-4 focus:outline-none', className)}
    >
      {children}
    </TabsPrimitive.Content>
  );
}
